import axios from 'axios';

export const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://crm.pejvakrayane.com/ajax/';
export const BASE_MEDIA_URL = process.env.REACT_APP_BASE_MEDIA_URL || 'https://crm.pejvakrayane.com/media/';

export default class ApiClient {
  constructor({ redirect = '/', noRedirect = true } = {}) {
    const baseURL = BASE_URL;
    this._defaultOn403 = { redirect, noRedirect };
    this._on403 = { ...this._defaultOn403 };

    this.axios = axios.create({
      baseURL,
      withCredentials: true,
      timeout: 30000,
    });

    this._initInterceptors();
  }

  _getCookie(name) {
    return document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${name}=`))
      ?.split('=')[1];
  }

  // Hits the proxied Django root once to receive the csrftoken cookie.
  async _ensureCsrf() {
    if (this._csrfFetched) return;
    this._csrfFetched = true;
    try {
      await fetch('/csrf-init/', { credentials: 'include' });
    } catch {}
  }

  _initInterceptors() {
    const MUTATING = new Set(['post', 'put', 'patch', 'delete']);

    this.axios.interceptors.request.use(async (config) => {
      const method = (config.method || 'get').toLowerCase();

      // Always attach session_id when the JS cookie is readable - checkLogin
      // accepts it from GET/POST as a fallback if the HttpOnly cookie is gone.
      const sessionId = this._getCookie('session_id');
      if (sessionId) {
        config.params = { ...config.params, session_id: sessionId };
      }

      if (MUTATING.has(method)) {
        // Ensure the Django csrftoken cookie is set before the first POST.
        await this._ensureCsrf();

        const csrfToken = this._getCookie('csrftoken');
        if (csrfToken) {
          config.headers['X-CSRFToken'] = csrfToken;
        }

        if (sessionId && config.data instanceof FormData) {
          config.data.append('session_id', sessionId);
        }
      }

      return config;
    });

    this.axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 403) {
          const perRequest = err.config?._noRedirect;
          const { redirect, noRedirect } = this._on403;
          this._reset403();

          if (!noRedirect && !perRequest) {
            window.location.href = redirect;
          }
        }
        return Promise.reject(err);
      }
    );
  }

  _reset403() {
    this._on403 = { ...this._defaultOn403 };
  }

  set403({ redirect = '/', noRedirect = false } = {}) {
    this._on403 = { redirect, noRedirect };
    return this;
  }

  get(url, config = {}) {
    return this.axios.get(url, config);
  }

  post(url, data = {}, config = {}) {
    return this.axios.post(url, data, config);
  }

  put(url, data = {}, config = {}) {
    return this.axios.put(url, data, config);
  }

  patch(url, data = {}, config = {}) {
    return this.axios.patch(url, data, config);
  }

  delete(url, config = {}) {
    return this.axios.delete(url, config);
  }
}
export const client = new ApiClient();